import api from '../utils/api';

/**
 * Upload a profile picture to the backend (local disk storage)
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} The public URL of the uploaded image
 */
export const uploadImageFile = async (file) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }

  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('Image size must be less than 2MB');
  }

  const formData = new FormData();
  formData.append('profilePicture', file);

  try {
    const response = await api.post('/api/upload/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.url;
  } catch (error) {
    console.error('Upload error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to upload image');
  }
};

/**
 * Upload a general image to the backend (local disk storage)
 * @param {File} file - The image file
 * @param {string} folder - Optional folder name under uploads/
 * @returns {Promise<string>} Public URL
 */
export const uploadImage = async (file, folder = 'general') => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }

  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('Image size must be less than 5MB');
  }

  const formData = new FormData();
  formData.append('image', file);
  formData.append('folder', folder);

  try {
    const response = await api.post('/api/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.url;
  } catch (error) {
    console.error('Upload error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to upload image');
  }
};

/**
 * Delete an image from local storage
 * @param {string} key - The storage key (relative path under uploads/)
 * @returns {Promise<void>}
 */
export const deleteImage = async (key) => {
  try {
    await api.delete('/api/upload/image', {
      data: { key },
    });
  } catch (error) {
    console.error('Delete error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to delete image');
  }
};

/**
 * Upload an attachment (PDF, Docs, etc.) through the backend API.
 * Files are stored on the server disk volume. Progress uses XHR upload events.
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Optional progress callback (receives 0-100 percentage)
 * @param {string} folder - Optional folder name (defaults to 'attachments')
 * @returns {Promise<string>} The URL of the uploaded file
 */
export const uploadAttachment = async (file, onProgress, folder = 'attachments') => {
  const maxSize = 100 * 1024 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('File size must be less than 100GB');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  try {
    const response = await api.post('/api/upload/attachment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (typeof onProgress === 'function' && event.total) {
          const percentCompleted = Math.round((event.loaded * 100) / event.total);
          onProgress(percentCompleted);
        }
      },
    });

    return response.url;
  } catch (error) {
    console.error('Upload error:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to upload attachment');
  }
};

export default {
  uploadImageFile,
  uploadImage,
  uploadAttachment,
  deleteImage,
};
